import { motion } from 'framer-motion';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { fadeInUp, staggerContainer, subtleHover } from '../utils/animations';
import { 
  Code, 
  Database, 
  Globe, 
  TrendingUp,
  Sparkles
} from 'lucide-react';

const Skills = () => {
  const { ref, controls } = useScrollReveal();

  const skillCategories = [
    {
      title: 'Frontend',
      icon: Globe,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      skills: [
        { name: 'HTML', src: 'https://cdn.simpleicons.org/html5/E34F26' },
        { name: 'CSS', src: 'https://cdn.simpleicons.org/css/1572B6' },
        { name: 'TailwindCSS', src: 'https://cdn.simpleicons.org/tailwindcss/38B2AC' },
        { name: 'React.js', src: 'https://cdn.simpleicons.org/react/61DAFB' },
        { name: 'JavaScript', src: 'https://cdn.simpleicons.org/javascript/F7DF1E' },
        { name: 'TypeScript', src: 'https://cdn.simpleicons.org/typescript/3178C6' },
      ]
    },
    {
      title: 'Backend',
      icon: Database,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
      skills: [
        { name: 'Node.js', src: 'https://cdn.simpleicons.org/nodedotjs/339933' },
        { name: 'Python', src: 'https://cdn.simpleicons.org/python/3776AB' },
      ]
    },
    {
      title: 'Database',
      icon: Database,
      color: 'from-orange-500 to-red-500',
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600',
      skills: [
        { name: 'SQLite3', src: 'https://cdn.simpleicons.org/sqlite/003B57' },
        { name: 'Prisma', src: 'https://cdn.simpleicons.org/prisma/143D53' },
        { name: 'Cloudflare D1', src: '/svg/Cloudflare-D1.svg' },
        { name: 'Cloudflare R2', src: '/svg/Cloudflare-R2.svg' },
      ]
    },
    {
      title: 'Cloud / DevOps',
      icon: Code,
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      skills: [
        { name: 'Docker', src: 'https://cdn.simpleicons.org/docker/2496ED' },
        { name: 'GitHub', src: 'https://cdn.simpleicons.org/github/181717' },
        { name: 'Git', src: 'https://cdn.simpleicons.org/git/F05032' },
        { name: 'GitLFS', src: 'https://cdn.simpleicons.org/gitlfs/F05032' },
      ]
    }
  ];


  return (
    <section id="skills" className="py-20 bg-neutral-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial="initial"
          animate={controls}
          variants={fadeInUp}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center mb-4">
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-800">
              Skills & Technologies
            </h2>
          </div>
          <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
          끊임없이 새로운 기술을 학습하고 프로젝트에 적용하며 성장하고 있습니다.
          </p>
        </motion.div>


        {/* Skill Categories */}
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate={controls}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {skillCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.title}
              variants={fadeInUp}
              transition={{ delay: categoryIndex * 0.1 }}
              whileHover={{ 
                scale: 1.02,
                transition: { duration: 0.2 }
              }}
              className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-neutral-100"
            >
              <div className="flex items-center mb-6">
                <div className={`w-12 h-12 ${category.bgColor} rounded-xl flex items-center justify-center mr-4`}>
                  <category.icon className={`w-6 h-6 ${category.iconColor}`} />
                </div>
                <h3 className="text-xl font-bold text-neutral-800">
                  {category.title}
                </h3>
              </div>

              <div className="space-y-4">
                {category.skills.map((skill, skillIndex) => (
                  <motion.div
                    key={skill.name}
                    initial={{ opacity: 1, x: 0 }}
                    animate={controls}
                    transition={{ delay: (categoryIndex * 0.1) + (skillIndex * 0.05) }}
                    className="space-y-2"
                  >
                    <div className="flex items-center">
                      <img
                        src={skill.src}
                        alt={skill.name}
                        className="w-5 h-5 mr-2 select-none"
                        loading="lazy"
                        decoding="async"
                      />
                      <span className="text-neutral-700 font-medium">
                        {skill.name}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
